<template>
  <div class="flex items-center gap-1 shrink-0">
    <!-- New chat -->
    <button type="button"
      class="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-primary transition-colors"
      aria-label="New chat" title="New chat"
      data-testid="ai-header-new-chat"
      @click="onNew">
      <i class="pi pi-plus text-sm"></i>
    </button>

    <!-- Recent chats -->
    <button type="button"
      class="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-primary transition-colors"
      aria-label="Recent chats" title="Recent chats"
      data-testid="ai-history-toggle"
      @click="toggleHistory">
      <i class="pi pi-history text-sm"></i>
    </button>

    <Popover ref="historyPanel" data-testid="ai-history-popover">
      <div class="w-72 max-h-80 flex flex-col">
        <AiConversationList
          :list="list"
          :activeId="activeId"
          @select="onSelect"
          @new="onNew"
          @refresh="emit('refresh')"
          @deleted="emit('deleted', $event)"
        />
      </div>
    </Popover>
  </div>
</template>

<script setup lang="ts">
import AiConversationList from '@/components/common/AiConversationList.vue';
import type { UseAiConversationsReturn } from '@/composables/useAiConversations';

/**
 * The chat window's header controls: New chat + a "recent chats" popover
 * wrapping AiConversationList.
 *
 * Shared by BOTH hosts — the floating panel and the full page — so browsing
 * and resuming behave identically wherever the chat is rendered, and a layout
 * change never forks the behaviour.
 */
defineProps<{
  list: UseAiConversationsReturn;
  activeId?: number | null;
}>();

const emit = defineEmits<{
  (e: 'select', id: number): void;
  (e: 'new'): void;
  (e: 'refresh'): void;
  (e: 'deleted', id: number): void;
}>();

const historyPanel = ref<{ toggle: (e: Event) => void; hide: () => void } | null>(null);

const toggleHistory = (event: Event) => {
  emit('refresh');
  historyPanel.value?.toggle(event);
};

const onNew = () => {
  historyPanel.value?.hide();
  emit('new');
};

const onSelect = (id: number) => {
  historyPanel.value?.hide();
  emit('select', id);
};
</script>
